import { useCallback } from "react";
import type { MapRef } from "react-map-gl";
import { toast } from "sonner";
import type { AiFilterResult } from "./marketplace-ai-filter-input";

export function useAiFilterMap() {
  const getMapRef = useCallback(
    () =>
      (window as unknown as { __marketplaceMapRef?: MapRef })
        .__marketplaceMapRef,
    []
  );

  const moveMap = (mapResult: AiFilterResult["map"]) => {
    const map = getMapRef();
    if (!(map && mapResult)) {
      return;
    }

    if (mapResult.bounds) {
      map.fitBounds(
        [
          [mapResult.bounds.minLng, mapResult.bounds.minLat],
          [mapResult.bounds.maxLng, mapResult.bounds.maxLat],
        ],
        { padding: 60, duration: 800 }
      );
    } else {
      map.flyTo({
        center: [mapResult.center.lng, mapResult.center.lat],
        zoom: mapResult.zoom ?? 10,
        duration: 400,
      });
    }
  };

  const handleNearMe = useCallback(
    (
      location: AiFilterResult["location"],
      onSuccess?: (coords: { latitude: number; longitude: number }) => void
    ) => {
      const map = getMapRef();
      if (!(location?.near_me && map && navigator.geolocation)) {
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          map.flyTo({
            center: [coords.longitude, coords.latitude],
            zoom: 12,
            duration: 400,
          });
          onSuccess?.(coords);
        },
        () => {
          toast.error(
            "Could not get your location. Please enable location services."
          );
        },
        { enableHighAccuracy: true }
      );
    },
    [getMapRef]
  );

  return { moveMap, handleNearMe };
}
